import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import API from '../utils/Axios.utils';
import useAuth from './useAuth';

// --- API Functions ---

const fetchDuplicatePairs = async (token) => {
  const config = { headers: { Authorization: `Bearer ${token}` } };
  const res = await API.get('/api/dedup/pairs', config);
  return res.data;
};

const triggerScanApi = async (token) => {
  const config = { headers: { Authorization: `Bearer ${token}` } };
  const res = await API.post('/api/dedup/scan', {}, config);
  return res.data;
};

const mergePairApi = async ({ pairId, primaryProfileId, token }) => {
  const config = { headers: { Authorization: `Bearer ${token}` } };
  const res = await API.post(`/api/dedup/pairs/${pairId}/merge`, { primaryProfileId }, config);
  return res.data;
};

const mergeClusterApi = async ({ primaryProfileId, secondaryProfileIds, token }) => {
  const config = { headers: { Authorization: `Bearer ${token}` } };
  const res = await API.post(`/api/dedup/cluster/merge`, { primaryProfileId, secondaryProfileIds }, config);
  return res.data;
};

const dismissPairApi = async ({ pairId, token }) => {
  const config = { headers: { Authorization: `Bearer ${token}` } };
  const res = await API.post(`/api/dedup/pairs/${pairId}/dismiss`, {}, config);
  return res.data;
};

// --- Hooks ---

export const useDuplicatePairs = () => {
  const { token } = useAuth();
  return useQuery({
    queryKey: ['duplicatePairs'],
    queryFn: () => fetchDuplicatePairs(token),
    enabled: !!token,
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

export const useTriggerScan = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => triggerScanApi(token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['duplicatePairs'] });
    },
  });
};

export const useMergePair = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pairId, primaryProfileId }) => mergePairApi({ pairId, primaryProfileId, token }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['duplicatePairs'] });
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      queryClient.refetchQueries({ queryKey: ['candidates'] });
    },
  });
};

export const useMergeCluster = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ primaryProfileId, secondaryProfileIds }) => mergeClusterApi({ primaryProfileId, secondaryProfileIds, token }),
    onMutate: async ({ clusterId, primaryProfileId, secondaryProfileIds }) => {
      await queryClient.cancelQueries({ queryKey: ['duplicatePairs'] });
      const previousData = queryClient.getQueryData(['duplicatePairs']);
      if (previousData) {
        queryClient.setQueryData(['duplicatePairs'], (old) => {
          if (!old) return old;
          const oldClusters = old.clusters || [];
          const remaining = oldClusters.filter(c => {
            if (clusterId && c.clusterId === clusterId) return false;
            if (c.candidates?.some(cand => cand.profile_id === primaryProfileId || (secondaryProfileIds || []).includes(cand.profile_id))) {
              return false;
            }
            return true;
          });
          return {
            ...old,
            clusters: remaining,
            count: remaining.length,
          };
        });
      }
      return { previousData };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['duplicatePairs'], context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['duplicatePairs'] });
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      queryClient.refetchQueries({ queryKey: ['candidates'] });
    },
  });
};

export const useDismissPair = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pairId }) => dismissPairApi({ pairId, token }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['duplicatePairs'] });
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      queryClient.refetchQueries({ queryKey: ['candidates'] });
    },
  });
};
